import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { Box, Container, Typography, useTheme, useMediaQuery, Button, CircularProgress, Alert, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useNavigate } from 'react-router-dom';
import { BusinessContext } from '../context/BusinessContext';
import { spacing } from '../utils/themeUtils';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import SubscriptionAlert from '../components/SubscriptionAlert';
import { formatCurrency } from '../utils/formatUtils';
import currencies from '../constants/currencies.json';
import BusinessesTable from '../components/dashboard/BusinessesTable';
import BulkAddBusinessesModal from '../components/dashboard/BulkAddBusinessesModal';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import { isDemoUser, setDemoBusinessSelections, getDemoBusinessSelections } from '../utils/demoUtils';

const API_URL = process.env.REACT_APP_API_URL;

const formatErrorMessage = (error) => {
  if (!error) return "";
  if (typeof error === 'string') return error;
  if (error.detail && typeof error.detail === 'string') return error.detail;
  if (error.detail && Array.isArray(error.detail)) return error.detail.map(e => e.msg || String(e)).join(', ');
  if (error.msg) return error.msg;
  if (Array.isArray(error)) return error.map(e => e.msg || String(e)).join(', ');
  try {
      const detailString = JSON.stringify(error.detail);
      if (detailString && detailString.length < 100) return detailString;
  } catch (e) { /* ignore stringify errors */ }
  return 'An unexpected error occurred.';
};

const Businesses = () => {
  const [businessesData, setBusinessesData] = useState([]);
  const { selectedBusinesses, updateSelectedBusinesses, refreshSelectedBusinesses, userEmail, isDemoMode } = useContext(BusinessContext);
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    subscription_status: null,
    trial_ends_at: null,
    is_active: false,
    user_role: null,
    subscription_plan: null,
    business_limit: 3, // Default to starter plan limit
  });
  
  const pollingIntervalRef = useRef(null);

  const [stopMonitoringDialogOpen, setStopMonitoringDialogOpen] = useState(false);
  const [businessToStopMonitoring, setBusinessToStopMonitoring] = useState(null);

  const [filterText, setFilterText] = useState('');

  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false);

  // Bulk unmonitor state
  const [bulkUnmonitorDialogOpen, setBulkUnmonitorDialogOpen] = useState(false);
  const [isBulkUnmonitoring, setIsBulkUnmonitoring] = useState(false);

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
  };

  const filteredBusinessesData = useMemo(() => {
    return businessesData.filter(business => {
      if (!business.is_monitored) {
        return false;
      }
      if (filterText && !business.name?.toLowerCase().includes(filterText.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [businessesData, filterText]);

  const selectedIdsSet = useMemo(() => 
      new Set(selectedBusinesses.map(b => b.id)), 
      [selectedBusinesses]
  );

  const eligibleBusinesses = useMemo(() => 
    filteredBusinessesData.filter(
    business => business.is_monitored && business.status !== 'pending' && subscriptionStatus.is_active
    ), 
    [filteredBusinessesData, subscriptionStatus.is_active]
  );
  const numEligible = eligibleBusinesses.length;
  const numSelected = eligibleBusinesses.filter(eligBiz => selectedIdsSet.has(eligBiz.id)).length;

  const isAllSelected = numEligible > 0 && numSelected === numEligible;
  const isIndeterminate = numSelected > 0 && numSelected < numEligible;

  const handleToggleSelect = useCallback(async (business, isCurrentlySelected) => {
    const newSelectedState = !isCurrentlySelected;
    
    const originalSelectedBusinesses = [...selectedBusinesses];
    const newSelectedBusinesses = newSelectedState
      ? [...selectedBusinesses, business]
      : selectedBusinesses.filter(b => b.id !== business.id);
    updateSelectedBusinesses(newSelectedBusinesses);

    // Skip API call for demo users - selections are local only
    if (isDemoMode) {
      setError(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user-business-association/${business.id}/select`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_selected: newSelectedState }),
      });

      if (!response.ok) {
        updateSelectedBusinesses(originalSelectedBusinesses);
        
        const errorData = await response.json().catch(() => null);
        setError(formatErrorMessage(errorData?.detail || 'Failed to update selection.'));
      } else {
         setError(null);
      }
    } catch (err) {
      updateSelectedBusinesses(originalSelectedBusinesses);

      setError('An error occurred while updating selection.');
    }
  }, [selectedBusinesses, updateSelectedBusinesses, isDemoMode, API_URL]);

  const handleSelectAllClick = useCallback(async (event) => {
    const targetState = event.target.checked;

    const originalSelectedBusinesses = [...selectedBusinesses];
    const updatesToAttempt = [];
    const newSelectedBusinessList = [];

    eligibleBusinesses.forEach(business => {
      const currentlySelected = selectedIdsSet.has(business.id);
      if (targetState && !currentlySelected) {
        updatesToAttempt.push({ id: business.id, newState: true });
        newSelectedBusinessList.push(business);
      } else if (!targetState && currentlySelected) {
        updatesToAttempt.push({ id: business.id, newState: false });
      } else if (currentlySelected) {
          newSelectedBusinessList.push(business);
      }
    });

    selectedBusinesses.forEach(selBiz => {
        if (!eligibleBusinesses.some(elig => elig.id === selBiz.id)) {
            newSelectedBusinessList.push(selBiz);
        }
    });

    updateSelectedBusinesses(newSelectedBusinessList);
  
    // Skip API calls for demo users - selections are local only
    if (isDemoMode) {
      setError(null);
      return;
    }
  
    if (updatesToAttempt.length > 0) {
      try {
        const results = await Promise.allSettled(
          updatesToAttempt.map(update =>
            fetch(`${API_URL}/user-business-association/${update.id}/select`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ is_selected: update.newState }),
            }).then(response => {
              if (!response.ok) {
                return response.json().then(err => { 
                    throw new Error(formatErrorMessage(err.detail || `Failed for ID ${update.id}`)); 
                });
              }
              return { id: update.id, status: 'fulfilled' }; 
            })
          )
        );
  
        const failedUpdates = results.filter(r => r.status === 'rejected');
  
        if (failedUpdates.length > 0) {
          updateSelectedBusinesses(originalSelectedBusinesses);

          const errorMessages = failedUpdates.map(f => f.reason?.message || `Update failed for an ID`).join('; ');
          setError(`Failed to update selection for ${failedUpdates.length} business(es). ${errorMessages}`);
    } else {
          setError(null);
        }
  
      } catch (error) {
         updateSelectedBusinesses(originalSelectedBusinesses);

        setError("An unexpected error occurred during bulk selection update.");
      }
    } else {
      setError(null);
    }
  }, [eligibleBusinesses, selectedBusinesses, selectedIdsSet, updateSelectedBusinesses, isDemoMode, API_URL]);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      // Fetch both subscription status and user info for role
      const [subResponse, userResponse] = await Promise.all([
        fetch(`${API_URL}/users/subscription-status`, { credentials: 'include' }),
        fetch(`${API_URL}/users/`, { credentials: 'include' })
      ]);
      
      if (subResponse.ok && userResponse.ok) {
        const [subData, userData] = await Promise.all([
          subResponse.json(),
          userResponse.json()
        ]);
        
        setSubscriptionStatus(prev => ({
          ...prev,
          subscription_status: subData.subscription_status,
          trial_ends_at: subData.trial_ends_at,
          is_active: subData.is_active,
          user_role: userData.role,
          subscription_plan: subData.subscription_plan,
          business_limit: subData.business_limit,
        }));
      } else {
        setSubscriptionStatus(prev => ({
          ...prev,
          subscription_status: null,
          trial_ends_at: null,
          is_active: false,
          user_role: null,
          subscription_plan: null,
          business_limit: 3,
        }));
      }
    } catch (error) {
      setSubscriptionStatus(prev => ({
        ...prev,
        subscription_status: null,
        trial_ends_at: null,
        is_active: false,
        user_role: null,
        subscription_plan: null,
        business_limit: 3,
      }));
    }
  }, [API_URL]);

  const fetchBusinesses = useCallback(async (isPollingUpdate = false) => {
    try {
      const response = await fetch(`${API_URL}/businesses/`, { credentials: 'include' });
       if (!response.ok) {
          const errorText = await response.text();
          let detail = 'Failed to fetch businesses';
          try { detail = JSON.parse(errorText).detail || detail; } catch { /* ignore parsing error */ }
          throw new Error(detail); 
       }
      const fetchedData = await response.json() || [];
      
      setBusinessesData(fetchedData);

      if (error === 'Failed to fetch businesses') {
      setError(null);
      }

    } catch (err) {
      if (!isPollingUpdate) {
          setError(err.message || 'Failed to fetch businesses');
      }
    }
  }, [API_URL, error]);

  const handleBusinessSelection = useCallback(async (business) => {
    if (!business.is_monitored) {
      return;
    }
    const isCurrentlySelected = selectedIdsSet.has(business.id);
    if (!isCurrentlySelected) {
      await handleToggleSelect(business, false); 
    }
  }, [handleToggleSelect, selectedIdsSet]);

  useEffect(() => {
    let isMounted = true; // Prevent state updates on unmounted component
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null); 
      try {
        // Fetch both subscription status and businesses data
        await Promise.all([
          fetchSubscriptionStatus(),
          fetchBusinesses()
        ]);
      } catch (initialError) {
      } finally {
         if (isMounted) { 
             setLoading(false);
         }
      }
    };
    fetchInitialData();
    return () => { isMounted = false; }; // Cleanup function
  }, [fetchSubscriptionStatus, fetchBusinesses]);

  useEffect(() => {
    const needsPolling = businessesData.some(business =>
        business.status === 'pending' || 
        [
            'pending_analysis',
            'analyzing',
            'pending_grouping',
            'grouping'
        ].includes(business.processing_status)
    );
    if (needsPolling && pollingIntervalRef.current === null) {
      pollingIntervalRef.current = setInterval(() => {
        fetchBusinesses(true);
      }, 5000);
    } else if (!needsPolling && pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [businessesData, fetchBusinesses]); 

  // Auto-open add business modal for new users with no businesses
  useEffect(() => {
    if (
      !loading && // Data has finished loading
      !hasAutoOpenedModal && // Haven't already auto-opened
      subscriptionStatus.is_active && // User has active subscription
      businessesData.length === 0 && // No businesses exist
      !isBulkAddModalOpen // Modal isn't already open
    ) {
      setIsBulkAddModalOpen(true);
      setHasAutoOpenedModal(true);
    }
  }, [loading, hasAutoOpenedModal, subscriptionStatus.is_active, businessesData.length, isBulkAddModalOpen]);

  const handleStopMonitoringClick = (business) => {
    setBusinessToStopMonitoring(business);
    setStopMonitoringDialogOpen(true);
  };

  const handleConfirmStopMonitoring = async () => {
    if (!businessToStopMonitoring) return;
    const businessToRemove = businessToStopMonitoring;
    const originalSelectedBusinesses = [...selectedBusinesses];
    
    updateSelectedBusinesses(prevSelected => prevSelected.filter(b => b.id !== businessToRemove.id));
    setStopMonitoringDialogOpen(false);
    setBusinessToStopMonitoring(null);
    
    try {
      const response = await fetch(`${API_URL}/businesses/${businessToRemove.id}/monitor`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_monitored: false }),
      });
      if (!response.ok) {
        updateSelectedBusinesses(originalSelectedBusinesses);

        const errorData = await response.json().catch(() => ({ detail: 'Failed to stop monitoring.' }));
        setError(formatErrorMessage(errorData)); 
      } else {
         setError(null);
         fetchBusinesses(); 
      }
    } catch (error) {
       updateSelectedBusinesses(originalSelectedBusinesses);

      setError('An error occurred while stopping monitoring. Please try again.');
    }
  };

  const handleCloseStopMonitoringDialog = () => {
    setStopMonitoringDialogOpen(false);
    setBusinessToStopMonitoring(null);
  };

  const handleBulkUnmonitorClick = () => {
    setBulkUnmonitorDialogOpen(true);
  };

  const handleConfirmBulkUnmonitor = async () => {
    if (selectedBusinesses.length === 0) return;
    
    setIsBulkUnmonitoring(true);
    const businessIdsToUnmonitor = selectedBusinesses.map(b => b.id);
    const originalSelectedBusinesses = [...selectedBusinesses];
    
    try {
      const response = await fetch(`${API_URL}/businesses/bulk-unmonitor`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(businessIdsToUnmonitor),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to bulk unmonitor businesses.' }));
        setError(formatErrorMessage(errorData));
      } else {
        const result = await response.json();
        setError(null);
        // Clear selected businesses since they're now unmonitored
        updateSelectedBusinesses([]);
        // Refresh the businesses list
        await fetchBusinesses();
        // Show success message
        setError(null);
      }
    } catch (error) {
      setError('An error occurred while bulk unmonitoring businesses. Please try again.');
    } finally {
      setIsBulkUnmonitoring(false);
      setBulkUnmonitorDialogOpen(false);
    }
  };

  const handleCloseBulkUnmonitorDialog = () => {
    setBulkUnmonitorDialogOpen(false);
  };

  const handleBulkAddSuccess = useCallback(async () => {
    // Refresh the businesses list and selected businesses from backend
    await Promise.all([
      fetchBusinesses(), // Refresh business list
      refreshSelectedBusinesses() // Refresh selections from backend
    ]);
    setIsBulkAddModalOpen(false); // Close modal on success
  }, [fetchBusinesses, refreshSelectedBusinesses]);
  // Get business limit based on subscription plan
  const getBusinessLimit = useCallback(() => {
    // Use the business_limit from the API response if available
    if (subscriptionStatus.business_limit) {
      return subscriptionStatus.business_limit;
    }
    
    // Fallback to local calculation (keeping for backwards compatibility)
    const planLimits = {
      'solo': 1,
      'starter': 3,
      'growth': 10, 
      'professional': 100
    };
    
    // For demo/trial users, use starter plan limit
    if (subscriptionStatus.subscription_status === 'trialing') {
      return planLimits.starter;
    }
    
    // Use plan-specific limit or default to starter
    return planLimits[subscriptionStatus.subscription_plan] || 3;
  }, [subscriptionStatus]);

  const businessLimit = getBusinessLimit();
  const currentBusinessCount = businessesData.filter(b => b.is_monitored).length;
  const isAtBusinessLimit = subscriptionStatus.user_role === 'admin' ? false : currentBusinessCount >= businessLimit;

  return (
    <Box sx={{
      minHeight: '100%',
      background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
      display: 'flex',
      flexDirection: 'column',
                      px: spacing.pageHorizontal,
        pt: spacing.pageTop,
        pb: spacing.pageBottom,
      opacity: loading ? 0.5 : 1,
      animation: 'fadeIn 1s forwards',
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      }
    }}>
      <Container maxWidth={false} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: { xs: 'flex-start', md: 'stretch' },
          width: '100%'
        }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 auto' }, px: spacing.headerHorizontal, order: { xs: 1, md: 1 } }}>
            <Typography
               variant="h3"
               component="h1"
               sx={{
                 fontWeight: 800,
                 fontSize: { xs: '1.75rem', lg: '2.5rem' },
                 color: '#0A2540',
                 letterSpacing: '-0.02em',
                 mb: { xs: 1, lg: 2 },
                 display: 'flex',
                 alignItems: 'center',
                 gap: { xs: 1, lg: 2 }
               }}
            >
               <StorefrontIcon sx={{ fontSize: 'inherit', color: '#1976d2' }} />
               Businesses
            </Typography>
            <Typography variant="body1" sx={{ color: '#546E7A', fontSize: { xs: '0.95rem', lg: '1.1rem' } }}>
              Select one or more businesses to view analytics and review insights
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, mt: { xs: 2, md: 2 } }}>
              <Tooltip 
                title={isDemoMode ? "Demo mode - adding businesses disabled" : ""} 
                arrow
              >
                <span>
                  <Button 
                      variant="contained" 
                      startIcon={<AddCircleOutlineIcon />} 
                      onClick={() => setIsBulkAddModalOpen(true)} 
                      disabled={isDemoMode || !subscriptionStatus.is_active || isAtBusinessLimit}
                      sx={(theme) => ({
                        alignSelf: { xs: 'flex-start', md: 'flex-start' },
                        borderRadius: '12px',
                        padding: theme.spacing(1, 2.5),
                        fontWeight: 600,
                        opacity: (isDemoMode || !subscriptionStatus.is_active || isAtBusinessLimit) ? 0.5 : 1,
                        cursor: (isDemoMode || !subscriptionStatus.is_active || isAtBusinessLimit) ? 'not-allowed' : 'pointer',
                      })}
                  >
                      Add Businesses
                  </Button>
                </span>
              </Tooltip>
              
              {/* Bulk Unmonitor Button - Only for admin users when businesses are selected */}
              {subscriptionStatus.user_role === 'admin' && selectedBusinesses.length > 0 && (
                <Tooltip title="Remove selected businesses from monitoring" arrow>
                  <span>
                    <Button 
                        variant="outlined" 
                        color="error"
                        startIcon={<RemoveIcon />}
                        onClick={handleBulkUnmonitorClick}
                        disabled={isBulkUnmonitoring}
                        sx={(theme) => ({
                          alignSelf: { xs: 'flex-start', md: 'flex-start' },
                          borderRadius: '12px',
                          padding: theme.spacing(1, 2.5),
                          fontWeight: 600,
                          opacity: isBulkUnmonitoring ? 0.5 : 1,
                          cursor: isBulkUnmonitoring ? 'not-allowed' : 'pointer',
                        })}
                    >
                        {isBulkUnmonitoring ? 'Removing...' : `Remove Selected (${selectedBusinesses.length})`}
                    </Button>
                  </span>
                </Tooltip>
              )}
              
                             {/* Help text with email link - only show when Add Businesses button is enabled */}
               {!(isDemoMode || !subscriptionStatus.is_active || isAtBusinessLimit) && (
               <Typography 
                 variant="body2" 
                 sx={{ 
                   color: 'text.secondary',
                   fontSize: { xs: '0.875rem', sm: '0.9rem' },
                   maxWidth: { xs: '100%', sm: '300px' }
                 }}
               >
                 Or{' '}
                 <Typography
                   component="a"
                   href={`mailto:info@sashy.ai?subject=Please Add My Business(es) to Sashy&body=Hi Sashy team,%0D%0A%0D%0APlease add my business(es) to Sashy for me. Here are the details:%0D%0A%0D%0ABusiness Name:%0D%0AGoogle Maps Link:%0D%0ARevenue:%0D%0AProducts/Services:%0D%0A%0D%0AThanks!`}
                   sx={{
                     color: 'primary.main',
                     textDecoration: 'underline',
                     cursor: 'pointer',
                     '&:hover': {
                       color: 'primary.dark',
                     }
                   }}
                 >
                   let our team do it for you
                 </Typography>
               </Typography>
               )}
            </Box>
            
            {/* Show limit information */}
            {subscriptionStatus.is_active && (
              <Box sx={{ 
                mt: 1, 
                display: 'flex', 
                alignItems: 'center', 
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }} 
                >
                  {subscriptionStatus.user_role === 'admin' 
                    ? `${currentBusinessCount} businesses monitored`
                    : `${currentBusinessCount} of ${businessLimit} businesses monitored`
                  }
                </Typography>
                {isAtBusinessLimit && subscriptionStatus.user_role !== 'admin' && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${process.env.REACT_APP_API_URL}/payments/customer-portal`, {
                          method: "POST",
                          credentials: "include",
                        });
                        const data = await res.json();
                        if (data.url) {
                          window.location.href = data.url;
                        } else {
                          console.error("No URL returned from customer portal endpoint");
                        }
                      } catch (error) {
                        console.error("Error creating customer portal session:", error);
                      }
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      p: 0,
                      color: 'primary.main',
                      minHeight: 'auto',
                      '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    Upgrade to add more businesses
                  </Button>
                )}
              </Box>
            )}

            {isXsScreen && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                {/* Mobile Button removed */}
              </Box>
            )}
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33.33%' }, px: spacing.headerHorizontal, order: { xs: 2, md: 2 }, display: { xs: 'none', md: 'block' } }}>
            {/* Empty */}
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33.33%' }, px: spacing.headerHorizontal, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', order: { xs: 3, md: 3 }, height: '100%' }}>
             {/* Desktop Button removed */}
          </Box>
        </Box>

         {!isDemoMode && (
             <SubscriptionAlert 
                status={subscriptionStatus.subscription_status} 
                trialEndsAt={subscriptionStatus.trial_ends_at}
                isActive={subscriptionStatus.is_active}
                userRole={subscriptionStatus.user_role}
             />
         )}

        <BulkAddBusinessesModal 
            open={isBulkAddModalOpen}
            onClose={() => setIsBulkAddModalOpen(false)}
            onBulkAddSuccess={handleBulkAddSuccess}
            API_URL={API_URL}
            currencies={currencies}
            formatErrorMessage={formatErrorMessage}
            subscriptionStatus={subscriptionStatus}
            currentBusinessCount={currentBusinessCount}
            businessLimit={businessLimit}
        />

        <Dialog
          open={stopMonitoringDialogOpen}
          onClose={handleCloseStopMonitoringDialog}
        >
          <DialogTitle>Remove "{businessToStopMonitoring?.name}"?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This action will remove the business from your account. You can add it again later if needed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStopMonitoringDialog}>Cancel</Button>
            <Button onClick={handleConfirmStopMonitoring} color="error" variant="contained">Remove</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={bulkUnmonitorDialogOpen}
          onClose={handleCloseBulkUnmonitorDialog}
        >
          <DialogTitle>Remove {selectedBusinesses.length} Selected Business{selectedBusinesses.length !== 1 ? 'es' : ''}?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This action will remove {selectedBusinesses.length} business{selectedBusinesses.length !== 1 ? 'es' : ''} from monitoring. 
              {selectedBusinesses.length > 1 && (
                <>
                  <br /><br />
                  <strong>Selected businesses:</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {selectedBusinesses.slice(0, 5).map(business => (
                      <li key={business.id}>{business.name}</li>
                    ))}
                    {selectedBusinesses.length > 5 && (
                      <li>... and {selectedBusinesses.length - 5} more</li>
                    )}
                  </ul>
                </>
              )}
              <br /><br />
              You can add them again later if needed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkUnmonitorDialog}>Cancel</Button>
            <Button 
              onClick={handleConfirmBulkUnmonitor} 
              color="error" 
              variant="contained"
              disabled={isBulkUnmonitoring}
            >
              {isBulkUnmonitoring ? 'Removing...' : `Remove ${selectedBusinesses.length} Business${selectedBusinesses.length !== 1 ? 'es' : ''}`}
            </Button>
          </DialogActions>
        </Dialog>

        {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>{formatErrorMessage(error)}</Alert>}

        <Box sx={{ mt: 2, alignItems: 'baseline', position: 'relative' }}>
          <BusinessesTable
            filteredBusinesses={filteredBusinessesData}
            filterText={filterText}
            onFilterChange={handleFilterChange}
            eligibleBusinesses={eligibleBusinesses}
            selectedIdsSet={selectedIdsSet}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            subscriptionStatus={subscriptionStatus}
            onStopMonitoringClick={handleStopMonitoringClick}
            onToggleSelect={handleToggleSelect}
            onSelectAllClick={handleSelectAllClick}
            formatErrorMessage={formatErrorMessage}
            API_URL={API_URL}
            currencies={currencies}
            isSmallScreen={isSmallScreen}
            loading={loading}
            isDemoMode={isDemoMode}
          />
          {subscriptionStatus.is_active && subscriptionStatus.user_role !== 'admin' && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ mt: 2, color: 'text.secondary' }} 
            >
              Your plan allows you to monitor up to {businessLimit} businesses simultaneously.
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Businesses;
